import React, { useState, useEffect } from 'react';
import {
    Box, Stack, Typography, Divider, Chip, IconButton, Popover,
    FormControlLabel, Switch, Select, MenuItem, FormControl, InputLabel,
    SelectChangeEvent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useTBContext } from '../Contexts/useTBStates';
import { useAuth } from 'react-oidc-context';
import CreateApi from '../../shared/Settings/appSettings';
import { setAppTheme } from '../../shared/Settings/appTheme';
import { formatDateForDisplay } from "@moment-golf/shared";
import { StyledTextboxInput } from '../../shared/Components/StyledTextboxInput';
import { StyledIconButton } from '../../shared/Components/StyledIconButton';
import { GroupEditor } from './GroupEditor';
import type { EventGroup, EventGroupType } from '@moment-golf/types';

interface GroupsManagerProps {
    appDims: any;
    compHeights: any;
}

const theme = setAppTheme(1);

const GROUP_TYPES: EventGroupType[] = ['series', 'trip', 'league', 'other'];

const blankNewGroup = {
    groupName: '',
    groupType: 'series' as EventGroupType,
    description: '',
    city: null as string | null,
    state: null as string | null,
    published: false,
    privacyGroup: null as string | null,
    privacyRegType: null as string | null,
    privacyViewType: null as string | null,
    privacyAccessType: null as string | null,
};

// Helper to format date range for a collection (earliest start to latest end of linked events)
function formatCollectionDateRange(eventIDs: string[] | undefined, events: any[]): string {
    if (!eventIDs?.length || !events?.length) return '—';
    const linked = events.filter((e: any) => e._id && eventIDs.includes(e._id));
    if (linked.length === 0) return '—';
    const minStart = new Date(Math.min(...linked.map((e: any) => new Date(e.startDate).getTime())));
    const maxEnd = new Date(Math.max(...linked.map((e: any) => new Date(e.endDate).getTime())));
    const opts = { year: '2-digit' as const, month: 'numeric' as const, day: 'numeric' as const };
    const startStr = formatDateForDisplay(minStart, opts);
    const endStr = formatDateForDisplay(maxEnd, opts);
    return startStr === endStr ? startStr : `${startStr} - ${endStr}`;
}

// Helper to extract options array from optionsLoaded by keyName
function getOptions(optionsLoaded: unknown[], keyName: string): string[] {
    const entry = optionsLoaded?.find((o: any) => o?.keyName === keyName) as { options?: { group: string }[] } | undefined;
    return entry?.options?.map(o => o.group) ?? [];
}

export const GroupsManager = ({ appDims, compHeights }: GroupsManagerProps) => {

    const auth = useAuth();
    const api = CreateApi();
    const { factor, appTheme, groupData, setGroupData, viewGroup, setViewGroup, setGroupTabValue, optionsLoaded, userData, eventData, setEventData, groupTypeFilter, setGroupTypeFilter } = useTBContext();

    // Load user events when showing the list so we can display date ranges for each collection
    useEffect(() => {
        if (viewGroup) return; // Skip when viewing a single collection
        const uid = auth.user?.profile?.sub;
        if (!uid) return;
        api.get('/tb/GetAllUserEvents', { params: { userID: uid, referenceDate: '1970-01-01T00:00:00.000Z' } })
            .then((res: { data: any[] }) => {
                if (Array.isArray(res.data)) setEventData(res.data);
            })
            .catch((err) => console.error('Error fetching events for collections list:', err));
    }, [auth.user?.profile?.sub, viewGroup]);

    const privacyGroupOptions = getOptions(optionsLoaded as unknown[], 'PrivacyGroup');
    const privacyRegTypeOptions = getOptions(optionsLoaded as unknown[], 'PrivacyRegType');
    const privacyViewTypeOptions = getOptions(optionsLoaded as unknown[], 'PrivacyViewType');
    const privacyAccessTypeOptions = getOptions(optionsLoaded as unknown[], 'PrivacyAccessType');

    // filterType is driven by context so sidebar menu items and chip bar can pre-set it
    const filterType = groupTypeFilter;
    const setFilterType = setGroupTypeFilter;
    const [newGroupAnchor, setNewGroupAnchor] = useState<HTMLElement | null>(null);
    const [newGroup, setNewGroup] = useState(blankNewGroup);
    const [saving, setSaving] = useState(false);
    const newGroupButtonRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchGroups();
    }, []);

    async function fetchGroups() {
        try {
            const res = await api.get<EventGroup[]>('/tb/GetAllGroups', {
                params: { userID: auth.user?.profile.sub }
            });
            setGroupData(res.data);
        } catch (err) {
            console.error('Error fetching groups:', err);
        }
    }

    async function handleAddGroup() {
        if (!newGroup.groupName.trim()) return;
        setSaving(true);
        try {
            const userID = auth.user?.profile.sub as string;
            const res = await api.post<EventGroup>('/tb/AddGroup', {
                userID,
                ownerID: userID,
                ownerName: (userData as { displayName?: string })?.displayName ?? '',
                groupName: newGroup.groupName.trim(),
                groupType: newGroup.groupType,
                description: newGroup.description || null,
                city: newGroup.city || null,
                state: newGroup.state || null,
                eventIDs: [],
                sharedUsers: [],
                active: true,
                published: newGroup.published,
                privacyGroup: newGroup.privacyGroup,
                privacyRegType: newGroup.privacyRegType,
                privacyViewType: newGroup.privacyViewType,
                privacyAccessType: newGroup.privacyAccessType,
            });
            setGroupData([...groupData, res.data]);
            setNewGroup(blankNewGroup);
            setNewGroupAnchor(null);
        } catch (err) {
            console.error('Error adding group:', err);
        } finally {
            setSaving(false);
        }
    }

    const filteredGroups = filterType === 'all'
        ? groupData
        : groupData.filter(g => g.groupType === filterType);

    if (viewGroup) {
        return (
            <GroupEditor
                appDims={appDims}
                compHeights={compHeights}
                returnToList={() => {
                    setViewGroup(null);
                    setGroupTabValue(0);
                }}
            />
        );
    }

    return (
        <Stack id='tb-editor-container' direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>

            {/* Header */}
            {/* <Stack direction={'column'} spacing={0.5} sx={{ height: 30 * factor, alignItems: 'center', mb: 2, padding: '10px 200px',
                backgroundColor: 'sepia.background', borderBottom: `1px solid ${theme.palette.primary.main}` }}>
                <Typography variant='h6' textAlign={'center'} sx={{ fontWeight: 700, width: '100%' }} color='primary.main'>
                    Collections Manager
                </Typography>
            </Stack> */}

            {/* Toolbar */}
            <Stack direction={'row'} spacing={2} sx={{ height: 30 * factor, padding: '0px 20px', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 1 }}>

                {/* Type filter chips */}
                {/* <Stack direction={'row'} spacing={1} alignItems={'center'}>
                    <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>Filter:</Typography>
                    {(['all', ...GROUP_TYPES] as const).map(type => (
                        <Chip
                            key={type}
                            label={type.charAt(0).toUpperCase() + type.slice(1)}
                            size='small'
                            color={filterType === type ? 'primary' : 'default'}
                            onClick={() => setFilterType(type)}
                            sx={{ textTransform: 'capitalize' }}
                        />
                    ))}
                </Stack> */}

                {/* New Collection button */}
                <Box ref={newGroupButtonRef}>
                    <StyledIconButton
                        appTheme={appTheme}
                        factor={factor}
                        color='primary'
                        icon={<AddIcon />}
                        label='New Collection'
                        onClick={() => setNewGroupAnchor(newGroupButtonRef.current)}
                    />
                </Box>
            </Stack>

            <Divider />

            {/* List area — matches TournamentManager list view dimensions */}
            <Box sx={{ padding: 2, height: compHeights.appUsableHeight - 200 * factor }}>
                <Box id='groups-list-stack' sx={{ width: '100%', height: compHeights.appUsableHeight - 200 * factor, padding: 0, overflowY: 'auto', scrollbarGutter: 'stable' }}>
                    <Stack direction={'row'} spacing={2}
                        sx={{
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            padding: '8px 15px',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: 'sepia.background',
                        }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 500, textAlign: 'left', flex: 5 }}>Name</Typography>
                        <Typography variant='subtitle2' sx={{ fontWeight: 500, textAlign: 'center', flex: 2 }}>Type</Typography>
                        <Typography variant='subtitle2' sx={{ fontWeight: 500, textAlign: 'center', flex: 3 }}>Date</Typography>
                        <Typography variant='subtitle2' sx={{ fontWeight: 500, textAlign: 'center', flex: 2 }}>Events</Typography>
                        <Typography variant='subtitle2' sx={{ fontWeight: 500, textAlign: 'center', flex: 2 }}>Published</Typography>
                        <Typography variant='subtitle2' sx={{ fontWeight: 500, textAlign: 'center', flex: 2 }}>Active</Typography>
                        <Box sx={{ width: 36, minWidth: 36, flexShrink: 0 }} />
                    </Stack>
                    <Stack direction={'column'} spacing={1} sx={{ paddingTop: 1 }}>
                        {filteredGroups.length === 0 ? (
                            <Typography variant='body2' color='text.secondary' sx={{ mt: 3, textAlign: 'center' }}>
                                No collections found. Create one to get started.
                            </Typography>
                        ) : (
                            filteredGroups.map((group) => (
                            <Stack
                                key={group._id}
                                direction={'row'} spacing={2}
                                alignItems={'center'}
                                onClick={() => { setViewGroup(group); setGroupTabValue(0); }}
                                sx={{
                                    padding: '8px 15px',
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.primary.light}`,
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'primary.extraLight' }
                                }}>
                                <Typography variant='body2' sx={{ flex: 5, fontWeight: 500, textAlign: 'left' }}>
                                    {group.groupName}
                                </Typography>
                                <Box sx={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
                                    <Chip
                                        label={group.groupType}
                                        size='small'
                                        sx={{ textTransform: 'capitalize', maxWidth: 80 }}
                                    />
                                </Box>
                                <Typography variant='body2' sx={{ flex: 3, textAlign: 'center', fontSize: '0.8rem' }}>
                                    {formatCollectionDateRange(group.eventIDs, eventData as any[])}
                                </Typography>
                                <Typography variant='body2' sx={{ flex: 2, textAlign: 'center' }}>
                                    {group.eventIDs?.length ?? 0}
                                </Typography>
                                <Box sx={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
                                    <Switch
                                        size='small'
                                        checked={group.published ?? false}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={async (e) => {
                                            const updated = { ...group, published: e.target.checked };
                                            setGroupData(groupData.map(g => g._id === group._id ? updated : g));
                                            await api.post('/tb/UpdateGroup', {
                                                _id: group._id,
                                                userID: auth.user?.profile.sub,
                                                published: e.target.checked,
                                            });
                                        }}
                                    />
                                </Box>
                                <Box sx={{ flex: 2, display: 'flex', justifyContent: 'center' }}>
                                    <Switch
                                        size='small'
                                        checked={group.active ?? true}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={async (e) => {
                                            const updated = { ...group, active: e.target.checked };
                                            setGroupData(groupData.map(g => g._id === group._id ? updated : g));
                                            await api.post('/tb/UpdateGroup', {
                                                _id: group._id,
                                                userID: auth.user?.profile.sub,
                                                active: e.target.checked,
                                            });
                                        }}
                                    />
                                </Box>
                                <Box sx={{ width: 36, minWidth: 36, flexShrink: 0 }} />
                            </Stack>
                        ))
                        )}
                    </Stack>
                </Box>
            </Box>

            {/* New Group popover */}
            <Popover
                open={Boolean(newGroupAnchor)}
                anchorEl={newGroupAnchor}
                onClose={() => { setNewGroupAnchor(null); setNewGroup(blankNewGroup); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Stack direction={'column'} spacing={2} sx={{ padding: 3, minWidth: 320 }}>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant='subtitle1' sx={{ fontWeight: 600 }} color='primary.main'>
                            New Collection
                        </Typography>
                        <IconButton size='small' onClick={() => { setNewGroupAnchor(null); setNewGroup(blankNewGroup); }}>
                            <CloseIcon fontSize='small' />
                        </IconButton>
                    </Stack>

                    <StyledTextboxInput
                        label='Collection Name'
                        value={newGroup.groupName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setNewGroup({ ...newGroup, groupName: e.target.value })}
                        appTheme={appTheme}
                        factor={factor}
                    />

                    <FormControl size='small' fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={newGroup.groupType}
                            label='Type'
                            onChange={(e: SelectChangeEvent) =>
                                setNewGroup({ ...newGroup, groupType: e.target.value as EventGroupType })}
                        >
                            {GROUP_TYPES.map(t => (
                                <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <StyledTextboxInput
                        label='Description (optional)'
                        value={newGroup.description}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setNewGroup({ ...newGroup, description: e.target.value })}
                        appTheme={appTheme}
                        factor={factor}
                    />

                    <FormControl size='small' fullWidth>
                        <InputLabel>Privacy Group</InputLabel>
                        <Select
                            value={newGroup.privacyGroup ?? ''}
                            label='Privacy Group'
                            onChange={(e: SelectChangeEvent) =>
                                setNewGroup({ ...newGroup, privacyGroup: e.target.value || null })}
                        >
                            <MenuItem value=''>— None —</MenuItem>
                            {privacyGroupOptions.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size='small' fullWidth>
                        <InputLabel>Registration Privacy</InputLabel>
                        <Select
                            value={newGroup.privacyRegType ?? ''}
                            label='Registration Privacy'
                            onChange={(e: SelectChangeEvent) =>
                                setNewGroup({ ...newGroup, privacyRegType: e.target.value || null })}
                        >
                            <MenuItem value=''>— None —</MenuItem>
                            {privacyRegTypeOptions.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size='small' fullWidth>
                        <InputLabel>Viewing Privacy</InputLabel>
                        <Select
                            value={newGroup.privacyViewType ?? ''}
                            label='Viewing Privacy'
                            onChange={(e: SelectChangeEvent) =>
                                setNewGroup({ ...newGroup, privacyViewType: e.target.value || null })}
                        >
                            <MenuItem value=''>— None —</MenuItem>
                            {privacyViewTypeOptions.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size='small' fullWidth>
                        <InputLabel>Access Method</InputLabel>
                        <Select
                            value={newGroup.privacyAccessType ?? ''}
                            label='Access Method'
                            onChange={(e: SelectChangeEvent) =>
                                setNewGroup({ ...newGroup, privacyAccessType: e.target.value || null })}
                        >
                            <MenuItem value=''>— None —</MenuItem>
                            {privacyAccessTypeOptions.map(opt => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={newGroup.published}
                                onChange={(e) => setNewGroup({ ...newGroup, published: e.target.checked })}
                            />
                        }
                        label='Published'
                    />

                    <StyledIconButton
                        appTheme={appTheme}
                        factor={factor}
                        color='primary'
                        icon={<></>}
                        label={saving ? 'Saving...' : 'Create Collection'}
                        onClick={handleAddGroup}
                        disabled={!newGroup.groupName.trim() || saving}
                    />
                </Stack>
            </Popover>
        </Stack>
    );
};

export default GroupsManager;
